export default function ChromeExtension() {
  const features = [
    'Extracts job data from 20+ job sites',
    'Detects ATS platform automatically',
    'Autofills 9+ ATS platforms in one click',
    'Captures recruiter/contact profiles from LinkedIn',
    'Syncs instantly with your JobOS dashboard',
    'Works without leaving the job posting'
  ];

  return (
    <section id="extension" className="py-[120px] bg-[#0A0E1A] relative overflow-hidden">
      {/* Subtle indigo gradient from left */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-[#6366F1]/10 to-transparent blur-[100px]"></div>
      
      <div className="relative max-w-[1280px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text Content */}
          <div>
            {/* Badge */}
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#141828] border border-[#2D3148] rounded-full text-[#94A3B8] text-sm font-medium">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-chrome-fill text-[#94A3B8] text-base"></i>
                </span>
                Free · Chrome Web Store
              </span>
            </div>

            {/* Headline */}
            <h2 className="text-5xl font-extrabold text-white mb-6 leading-tight">
              The extension that lives<br />
              in your browser toolbar.
            </h2>

            {/* Body */}
            <p className="text-[#94A3B8] text-lg leading-relaxed mb-8">
              Install the JobOS Chrome Extension and your browser becomes part of the
              OS. Browse any job site — LinkedIn, Indeed, Glassdoor, Rozee, Naukri —
              and JobOS works silently in the background. One click to capture. One click
              to autofill. No switching tabs. No copy-paste. Just apply.
            </p>

            {/* Feature List */}
            <ul className="space-y-3 mb-10">
              {features.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-[#10B981] text-lg mt-0.5">✓</span>
                  <span className="text-[#F8FAFC] text-base">{feature}</span>
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            <div>
              <a
                href="https://chrome.google.com/webstore"
                className="inline-flex items-center justify-center gap-2 h-14 px-10 bg-[#6366F1] text-white text-lg font-semibold rounded-full transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(99,102,241,0.4)] whitespace-nowrap cursor-pointer"
              >
                <span className="text-xl">⚡</span>
                Add to Chrome — It's Free
              </a>
              <p className="text-[#64748B] text-sm mt-4">
                Chrome Web Store · 1-click install · No account required to install
              </p>
            </div>
          </div>

          {/* Right: Extension Mockup */}
          <div className="relative">
            <div className="relative max-w-[480px] mx-auto">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#6366F1]/20 to-transparent blur-3xl"></div>
              
              {/* Extension Popup Mockup */}
              <div className="relative bg-[#141828] border border-[#2D3148] rounded-2xl shadow-[0_24px_64px_rgba(0,0,0,0.6)] overflow-hidden">
                {/* Header */}
                <div className="bg-[#0A0E1A] border-b border-[#2D3148] px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#4F46E5] rounded-lg flex items-center justify-center">
                      <span className="text-white text-xs font-bold">JO</span>
                    </div>
                    <span className="text-white text-base font-bold">
                      Job<span className="text-[#6366F1]">OS</span>
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-3 h-3 rounded-full bg-[#2D3148]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#2D3148]"></div>
                    <div className="w-3 h-3 rounded-full bg-[#2D3148]"></div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Job Badge */}
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-[#10B981] text-white text-xs font-bold uppercase rounded-md">
                      JOB
                    </span>
                  </div>

                  {/* Extracted Data */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <div className="text-[#64748B] text-xs font-mono uppercase mb-1">Job Title</div>
                      <div className="text-[#F8FAFC] text-base font-semibold">Senior Product Designer</div>
                    </div>
                    <div>
                      <div className="text-[#64748B] text-xs font-mono uppercase mb-1">Company</div>
                      <div className="text-[#F8FAFC] text-base font-semibold">Stripe</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-[#64748B] text-xs font-mono uppercase mb-1">Salary</div>
                        <div className="text-[#F8FAFC] text-base font-semibold">$140k–$180k</div>
                      </div>
                      <div>
                        <div className="text-[#64748B] text-xs font-mono uppercase mb-1">Location</div>
                        <div className="text-[#F8FAFC] text-base font-semibold">Remote</div>
                      </div>
                    </div>
                  </div>

                  {/* ATS Detection Banner */}
                  <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-2">
                      <span className="text-[#F59E0B] text-base">⚡</span>
                      <div>
                        <div className="text-[#F59E0B] text-sm font-semibold mb-1">ATS Detected: Workday</div>
                        <div className="text-[#F59E0B]/80 text-xs">Autofill Ready</div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-2">
                    <button className="w-full h-10 bg-[#6366F1] text-white text-sm font-semibold rounded-lg hover:bg-[#4F46E5] transition-colors cursor-pointer whitespace-nowrap">
                      Save to Tracker
                    </button>
                    <button className="w-full h-10 bg-transparent border border-[#2D3148] text-[#F8FAFC] text-sm font-semibold rounded-lg hover:border-[#6366F1] transition-colors cursor-pointer whitespace-nowrap">
                      Autofill Application
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}